import { DateTime } from "luxon"
import { Form } from "formik"
import { Formik } from "../../../../forms/SchemaFormik"
import { makeStyles } from "tss-react/mui"
import { Box } from "@mui/material"
import { buildDeliveryTimesSchema, earliestDeliveryTime, prepareInitialValues, sortedDeliveryTimes } from "./helpers"
import { useAppLocalization } from "../../../../../../modules/localization/components/AppLocalizationProvider"
import { SelectedPriceListItem } from "../../../createOrderTypes"
import { PopoverDeliveryTimeFormContent } from "./components/form/PopoverDeliveryTimeFormContent"
import { DeliveryOrderLine, DeliveryTimesField, DeliveryWithCart, DeliveryTimeDay } from "../../form/formTypes"
import { useDateTime } from "../../../../../../modules/localization/useDateTime"

const useStyles = makeStyles()((theme) => ({
    subtitle: {
        color: theme.palette.tailwind.slate[950],
    },
    content: {
        padding: "2rem 4rem",
        display: "flex",
        gap: "2rem",
        flexDirection: "column",
        color: theme.palette.tailwind.slate[950],
        width: "31rem",
    },
}))

export type DeliveryTimeValues = Readonly<{
    time: DateTime | null
    orderLines: DeliveryOrderLine[]
}>

export type CreateUpdateDeliveryFormValues = Readonly<{
    deliveryDate: DateTime | null
    deliveryTimes: DeliveryTimeValues[]
}>

type CreateUpdateDeliveryTimePopoverProps = Readonly<{
    initialValues?: DeliveryTimesField
    eventStartTime: DateTime | null
    priceList: SelectedPriceListItem
    onSubmit: (deliveries: DeliveryTimesField) => void
    allowedWeekdays: number[]
    closingDates: string[]
    canOverrideClosingDates: boolean
    grandfatheredDate?: DateTime | null
    openingHours: DeliveryTimeDay[]
    maxAllowedDeliveries: number
    handlePopoverClose: () => void
    allowPastDates: boolean
}>

export function CreateUpdateDeliveryTimePopover({
    initialValues,
    eventStartTime,
    priceList,
    onSubmit,
    allowedWeekdays,
    closingDates,
    canOverrideClosingDates,
    grandfatheredDate,
    openingHours = [],
    maxAllowedDeliveries,
    handlePopoverClose,
    allowPastDates,
}: CreateUpdateDeliveryTimePopoverProps) {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()
    const { l10n } = useAppLocalization()

    const validationSchema = buildDeliveryTimesSchema({
        l10n,
        dateTimeFactory,
        allowedWeekdays,
        closingDates,
        canOverrideClosingDates,
        grandfatheredDate,
        openingHours,
        allowPastDates,
    })

    const handleSubmit = (values: CreateUpdateDeliveryFormValues) => {
        const formattedDate = values.deliveryDate ? dateTimeFactory.toDate(values.deliveryDate) : null
        const formattedTimes: DeliveryWithCart[] = sortedDeliveryTimes(
            values.deliveryTimes.map((entry) => ({
                time: entry.time ? dateTimeFactory.toHourMinute(entry.time) : "",
                orderLines: entry.orderLines,
            })),
        )
        formattedDate &&
            onSubmit({
                deliveryDate: formattedDate,
                deliveryTimes: formattedTimes,
                activeTime: earliestDeliveryTime(formattedTimes),
            })
    }

    return (
        <Box className={classes.content}>
            <Formik<CreateUpdateDeliveryFormValues>
                enableReinitialize
                initialValues={prepareInitialValues(dateTimeFactory, eventStartTime, openingHours, initialValues)}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                <Form>
                    <PopoverDeliveryTimeFormContent
                        priceList={priceList}
                        allowedWeekdays={allowedWeekdays}
                        eventStartTime={eventStartTime}
                        closingDates={closingDates}
                        canOverrideClosingDates={canOverrideClosingDates}
                        grandfatheredDate={grandfatheredDate}
                        openingHours={openingHours}
                        maxAllowedDeliveries={maxAllowedDeliveries}
                        handlePopoverClose={handlePopoverClose}
                        allowPastDates={allowPastDates}
                    />
                </Form>
            </Formik>
        </Box>
    )
}
